import {
  beforeBreadcrumbFilter,
  enrichErrorEvent,
  initFaroRuntime,
} from './utils/init-tracing';
import { createApp } from 'vue';
import { createGtm } from '@gtm-support/vue-gtm';
import App from '@/components/App.vue';
import store from '@/store';
import router from '@/routes';
import VueSocialSharing from 'vue-social-sharing';
import VueApexCharts from 'vue3-apexcharts';
import { initSentry } from '@/utils/init-error-tracking';
import '@/utils/init-google-autocomplete';
import i18n from '@/plugins/i18n';
import { createMetaManager } from 'vue-meta';
import clickOutside from '@/plugins/directives/click-outside';
import onlyNumbers from '@/plugins/directives/only-numbers';
import { $filters } from '@/utils/init-filters';
import '@/assets/style/components/table-component.less';
import '@/assets/style/tailwind-utilities.css';
import 'primevue/resources/primevue.min.css';
import 'primeicons/primeicons.css';
import '@/assets/style/primevue-theme.css';
import PrimeVue from 'primevue/config';
import { en_US } from '@/utils/primevue-config';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import Tooltip from 'primevue/tooltip';
import { VueQueryPlugin } from 'vue-query';
import './assets/external/company_toolkit';
import { auth0 } from './utils/auth0';
import { ENV, isProduction, isStaging } from '../config/app-env';

import type { App as VueApp } from 'vue';

import FormInput from '@/components/forms/FormInput.vue';
import FormRow from '@/components/forms/FormRow.vue';
import ErrorMessage from '@/components/banners/ErrorMessage.vue';
import UploadFileWidget from '@/components/upload/UploadFileWidget.vue';
import MainIcon from '@/components/icons/MainIcon.vue';
import Popup from '@/components/popups/Popup.vue';
import VButton from '@/components/VButton.vue';

import Ripple from 'primevue/ripple';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const faro = initFaroRuntime(
  ENV.FARO_COLLECTOR_URL,
  ENV.FARO_APP_NAME,
  ENV.CLEARBANC_ENV,
  ENV.GIT_COMMIT_SHA,
);

if (
  import.meta.env.MODE !== 'mock' &&
  ENV.NODE_ENV === 'development' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname)
) {
  const url = new URL(window.location.href);
  url.hostname = 'local.clearbanc.com';
  url.port = url.port || '8080';
  url.protocol = 'https:';
  window.location.replace(url.toString());
  throw new Error(
    '[DEV] Redirecting to local.clearbanc.com from localhost or 127.0.0.1',
  );
}

async function bootstrap() {
  if (import.meta.env.MODE === 'mock') {
    await import('@tests/support/playwright/mocks/browser');
  }

  const metaManager = createMetaManager();
  const app: VueApp = createApp(App);
  initSentry(app, ENV.SENTRY_DSN, ENV.SENTRY_ENVIRONMENT, {
    beforeSend: enrichErrorEvent.bind(
      undefined,
      ENV.FARO_APP_ID,
      ENV.CLEARBANC_ENV,
    ),
    beforeBreadcrumb: beforeBreadcrumbFilter,
  });

  app.config.globalProperties.$filters = $filters;

  app.use(VueQueryPlugin);
  app.use(store);
  // https://github.com/auth0/auth0-vue/blob/main/EXAMPLES.md#protecting-a-route
  // router should be registered before auth0
  app.use(router);
  app.use(auth0);
  app.use(i18n);
  app.use(PrimeVue, { locale: en_US });
  app.provide('i18n', i18n);
  app.use(metaManager);
  app.directive('click-outside', clickOutside);
  app.directive('only-numbers', onlyNumbers);
  app.use(ConfirmationService);
  app.directive('ripple', Ripple);

  // Init Googs Tag Manager
  app.use(
    createGtm({
      id: 'GTM-P8XCP59',
      debug: true,
      loadScript: isProduction || isStaging,
      vueRouter: router,
      trackOnNextTick: false,
    }),
  );

  // init social sharing library
  app.use(VueSocialSharing);

  app.use(VueApexCharts);
  app.use(ToastService);
  app.directive('tooltip', Tooltip);

  app
    .component('form-input', FormInput)
    .component('form-row', FormRow)
    .component('error-message', ErrorMessage)
    .component('UploadFileWidget', UploadFileWidget)
    .component('icon', MainIcon)
    .component('popup', Popup)
    .component('v-button', VButton);

  router.isReady().then(() => app.mount('#app'));
}

bootstrap();
